import React from 'react';
import { logError } from '@utils';
import { useStreamStatus } from '@/hooks/utils/useStreamStatus';
import { createAnswerEntries } from './rfpTemplateAnswerUtils';
import { useRfpTemplateAnswers } from './useRfpTemplateAnswers';

const defaultContextValue = {
  answersLookup: {},
  answerEntries: [],
  streamStatus: 'pending',
  streamError: null,
  isComplete: false,
  resultId: null,
  streamingId: null,
  agentRecordId: null,
  templateRunId: null,
  hasStreamingPreview: false,
  templateId: null,
  selectedTemplate: null,
  sourceDocIds: [],
  queryListMetadata: [],
  isStreamingEnabled: true,
  resetResults: () => logError(new Error('resetResults not initialized')),
};

export const RfpTemplateResultsContext = React.createContext(defaultContextValue);

export function RfpTemplateResultsProvider({
  agentRecordId,
  children,
  onReset,
  queryListMetadata = [],
  resultId,
  streamingId,
  templateRunId,
  templateId,
  selectedTemplate,
  sourceDocIds = [],
  isStreamingEnabled = true,
}) {
  const previewStreamingId = isStreamingEnabled ? streamingId : null;
  const { answersLookup, streamError } = useRfpTemplateAnswers(previewStreamingId, queryListMetadata);
  const hasStreamingPreview = Boolean(previewStreamingId);
  const isAgentRun = Boolean(agentRecordId);
  const streamStatusOptions = React.useMemo(
    () => ({
      includeContext: true,
      includeAgentPlan: isAgentRun,
    }),
    [isAgentRun]
  );
  const streamStatus = useStreamStatus(resultId, streamStatusOptions);

  const answerEntries = React.useMemo(
    () => createAnswerEntries(queryListMetadata, answersLookup),
    [answersLookup, queryListMetadata]
  );

  const value = React.useMemo(
    () => ({
      answersLookup,
      answerEntries,
      streamStatus,
      streamError,
      agentRecordId,
      isComplete: streamStatus === 'complete',
      resultId,
      streamingId: previewStreamingId,
      templateRunId,
      hasStreamingPreview,
      templateId,
      selectedTemplate,
      sourceDocIds,
      queryListMetadata,
      isStreamingEnabled,
      resetResults: onReset,
    }),
    [
      answerEntries,
      agentRecordId,
      answersLookup,
      hasStreamingPreview,
      isStreamingEnabled,
      onReset,
      queryListMetadata,
      resultId,
      sourceDocIds,
      streamError,
      templateRunId,
      previewStreamingId,
      streamStatus,
      templateId,
      selectedTemplate,
    ]
  );

  return <RfpTemplateResultsContext.Provider value={value}>{children}</RfpTemplateResultsContext.Provider>;
}
